<template>
  <div class="pcd-account-menu">
  <button ref="trigger" type="button" class="pcd-wallet-chip pcd-account-trigger" :aria-label="t('開啟帳戶與設定')" aria-haspopup="dialog" :aria-expanded="show" :disabled="blocked" @click="open">
    <template v-if="account.address"><span class="pcd-wallet-network">{{ t(onPolygon ? 'Polygon' : '切換網路') }}</span><span>{{ shortAddress }}</span></template>
    <span v-else>{{ t(restoring ? '恢復中…' : '帳戶與設定') }}</span>
    <van-icon name="arrow-down" aria-hidden="true" />
  </button>
  <van-action-sheet :show="show" @update:show="value => { if (!value) close(); }" teleport="body" :z-index="11000" class="pcd-ui pcd-sheet pcd-account-sheet" :close-on-popstate="true" :aria-labelledby="titleId" aria-modal="true" @opened="focusFirst" @closed="restoreFocus" @keydown="handleKey">
    <div ref="content" class="pcd-dialog pcd-account-content">
      <div class="pcd-account-handle" aria-hidden="true"></div>
      <div class="pcd-dialog-heading"><h2 :id="titleId">{{ t('帳戶與設定') }}</h2><button type="button" class="pcd-icon-button" :aria-label="t('關閉帳戶與設定')" @click="close()"><van-icon name="cross" aria-hidden="true" /></button></div>
      <template v-if="account.address">
        <p class="pcd-muted">{{ t('錢包地址') }}</p>
        <div class="pcd-account-address-row"><button type="button" class="pcd-account-address" :aria-expanded="fullAddress" @click="fullAddress = !fullAddress">{{ shortAddress }}<span class="pcd-account-address-hint">{{ t(fullAddress ? '收起完整地址' : '查看完整地址') }}</span></button><button type="button" class="pcd-icon-button" :aria-label="t('複製地址')" :disabled="copying" @click="copyAddress"><van-icon name="description-o" aria-hidden="true" /></button></div>
        <p v-if="fullAddress" class="pcd-full-address pcd-account-full">{{ account.address }}</p>
      </template>
      <dl class="pcd-account-details"><div><dt>{{ t('連接狀態') }}</dt><dd :class="{ 'pcd-account-connected': !!account.address }">{{ t(account.address ? '已連接' : restoring ? '正在恢復錢包連接…' : '未連接') }}</dd></div><div v-if="account.address"><dt>{{ t('網路') }}</dt><dd>{{ onPolygon ? 'Polygon' : t('非 Polygon 網路') }}<span v-if="!onPolygon && account.chainId"> · {{ account.chainId }}</span></dd></div></dl>
      <p v-if="feedback" class="pcd-account-feedback" role="status">{{ t(feedback) }}</p>
      <button v-if="!account.address || !onPolygon" type="button" class="pcd-button pcd-button-secondary pcd-submit" :disabled="connectionLocked" @click="connect">{{ t(account.address ? '切換至 Polygon' : '連接錢包') }}</button>
      <div class="pcd-account-language"><LanguageSwitcher /><p class="pcd-muted">{{ t('套用至所有頁面') }}</p></div>
      <p v-if="accountOperationLocked || busy" class="pcd-account-feedback" role="status">{{ t('交易處理中，請勿重複提交。') }}</p>
      <router-link v-if="admin && account.address" v-slot="{ href, navigate }" to="/admin-dashboard" custom><a :href="href" class="pcd-button pcd-button-secondary pcd-submit" :aria-disabled="connectionLocked" :tabindex="connectionLocked ? -1 : undefined" @click="openAdmin($event, navigate)">{{ t('管理控制台') }}<van-icon name="arrow" aria-hidden="true" /></a></router-link>
      <button v-if="account.address" type="button" class="pcd-button pcd-disconnect pcd-submit pcd-account-disconnect" :disabled="connectionLocked" @click="disconnect">{{ t('斷開連接') }}</button>
    </div>
  </van-action-sheet>
  </div>
</template>
<script setup>
import { computed, getCurrentInstance, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { routeLocationKey } from 'vue-router';
import { t } from '@/i18n';
import { useWalletStore } from '@/stores/walletStore';
import { useWalletAccount } from '@/composables/useWalletAccount';
import { walletRecoveryState } from '@/composables/walletRecovery';
import { accountOperationLocked } from '@/composables/accountOperationLock';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import '@/styles/pcd-account.css';
const props = defineProps({ busy: Boolean, blocked: Boolean, admin: Boolean });
const wallet = useWalletStore(), account = useWalletAccount(), route = inject(routeLocationKey, null);
const show = ref(false), fullAddress = ref(false), copying = ref(false), feedback = ref('');
const trigger = ref(null), content = ref(null), operating = ref(false);
const titleId = `pcd-account-title-${getCurrentInstance().uid}`;
const onPolygon = computed(() => account.value.chainId === 137);
const restoring = computed(() => walletRecoveryState.value === 'restoring');
const shortAddress = computed(() => account.value.address ? account.value.address.slice(0, 6) + '…' + account.value.address.slice(-4) : '');
const connectionLocked = computed(() => props.busy || accountOperationLocked.value || wallet.isCheckState || operating.value || restoring.value);
let session = 0, restore = true, disposed = false;
function reset() { session++; feedback.value = ''; copying.value = false; fullAddress.value = false; }
function open() { if (props.blocked) return; reset(); restore = true; show.value = true; nextTick(focusFirst); }
function close(returnFocus = true) { restore = returnFocus; show.value = false; reset(); }
function focusFirst() { if (show.value && !disposed) content.value?.querySelector('button:not(:disabled)')?.focus(); }
function restoreFocus() {
  if (!restore || disposed || show.value || props.blocked) return;
  const active = document.activeElement;
  if (active === document.body || content.value?.closest('[role="dialog"]')?.contains(active)) trigger.value?.focus();
}
function containFocus(event) {
  if (show.value && content.value && !content.value.closest('[role="dialog"]')?.contains(event.target)) focusFirst();
}
watch(show, value => {
  if (value) document.addEventListener('focusin', containFocus);
  else document.removeEventListener('focusin', containFocus);
}, { flush: 'sync' });
function handleKey(event) {
  if (event.key === 'Escape') { event.preventDefault(); close(); return; }
  if (event.key !== 'Tab') return;
  const controls = [...content.value.querySelectorAll('button:not(:disabled), input:not(:disabled):checked, a[href]:not([aria-disabled="true"])')];
  const first = controls[0], last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
}
async function copyAddress() {
  if (!account.value.address || copying.value) return;
  const address = account.value.address, token = session;
  copying.value = true;
  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(address);
    if (token === session && account.value.address === address) feedback.value = '地址已複製';
  } catch {
    if (token === session && account.value.address === address) { feedback.value = '無法自動複製，請長按完整地址手動複製。'; fullAddress.value = true; }
  } finally { if (token === session) copying.value = false; }
}
async function connect() {
  if (connectionLocked.value) return;
  operating.value = true; close();
  try { await wallet.connectWallet(); } finally { operating.value = false; }
}
async function disconnect() {
  if (connectionLocked.value) return;
  operating.value = true; close();
  try { await wallet.disconnectWallet(); } finally { operating.value = false; }
}
function openAdmin(event, navigate) { if (connectionLocked.value) event.preventDefault(); else { close(false); navigate(event); } }
watch([() => account.value.address, () => account.value.chainId], () => close(false), { flush: 'sync' });
watch(() => route?.fullPath, () => close(false), { flush: 'sync' });
watch(() => props.blocked, value => { if (value) close(false); }, { flush: 'sync' });
onBeforeUnmount(() => { disposed = true; reset(); document.removeEventListener('focusin', containFocus); });
</script>
